/**
 * ETF Scores Service
 * Handles fetching and calculating ETF prediction scores
 * Uses existing backend predictions endpoint
 */

import config from '../config';

class ETFScoresService {
  /**
   * Fetch ETF scores data from existing backend predictions API
   * Returns promise that resolves to array of score objects
   */
  static async fetchETFScores() {
    try {
      console.log('🔍 ETF Scores: Fetching predictions from existing API...');
      
      const etfSymbols = ['TQQQ', 'UGL', 'IBIT', 'SOXL', 'FAS', 'TMF', 'LABU', 'UCO', 'IONQ', 'CEG', 'QBTS', 'MP', 'GOOG', 'TSLA', 'AMZN', 'AAPL', 'MSFT', 'NVDA', 'META', 'FTS', 'SPY', 'WSP.TO'];
      const etfDataMap = {};
      
      // Fetch 1d predictions for each ETF symbol
      for (const symbol of etfSymbols) {
        try {
          const params = new URLSearchParams();
          params.append('symbol', symbol);
          params.append('timeframe', '1d');  // 1d timeframe only
          params.append('model', 'gpt');     // CRITICAL: Only use 'gpt' model (matches IBKR desktop trader)
          params.append('limit', '30');      // Get last 30 to find best prediction

          const response = await fetch(`${config.api.API_URL}/api/predictions?${params}`, {
            credentials: 'include',
          });

          if (response.ok) {
            const data = await response.json();
            const predictions = data.predictions || [];
            console.log(`📊 ETF Scores [${symbol}]: Got ${predictions.length} predictions from model='gpt' timeframe='1d'`);
            
            if (predictions.length > 0) {
              etfDataMap[symbol] = predictions;
            }
          } else {
            console.warn(`⚠️ ETF Scores [${symbol}]: API returned ${response.status}`);
          }
        } catch (err) {
          console.error(`❌ Error fetching ${symbol}:`, err.message);
        }
      }

      console.log(`📊 ETF Scores: Fetched data for ${Object.keys(etfDataMap).length} symbols`);
      console.log('📊 ETF Scores: rawData keys:', Object.keys(etfDataMap).slice(0, 3), '...', 'Full map:', etfDataMap);
      
      return {
        scores: [],
        html: '',
        rawData: etfDataMap
      };
    } catch (error) {
      console.error('❌ Error in fetchETFScores:', error);
      return { scores: [], html: '', rawData: {} };
    }
  }

  /**
   * Legacy fallback method - fetch from predictions endpoint (UNUSED - kept for reference)
   */
  static async fetchETFScoresLegacy_UNUSED() {
    try {
      // Fetch predictions for all ETF symbols
      const etfSymbols = ['TQQQ', 'UGL', 'IBIT', 'SOXL', 'FAS', 'TMF', 'LABU', 'UCO', 'IONQ', 'CEG', 'QBTS', 'MP', 'GOOG', 'TSLA', 'AMZN', 'AAPL', 'MSFT', 'NVDA', 'META', 'FTS', 'SPY', 'WSP.TO'];
      const etfDataMap = {};

      // Fetch predictions for each symbol
      for (const symbol of etfSymbols) {
        try {
          const params = new URLSearchParams();
          params.append('symbol', symbol);
          params.append('limit', '100');  // Get enough predictions to have good coverage
          params.append('offset', '0');
          // Note: NOT filtering by timeframe initially - we'll sort them ourselves

          console.log(`🔍 ETF Scores (legacy): Fetching predictions for ${symbol}...`);
          const response = await fetch(`${config.api.API_URL}/api/predictions?${params}`, {
            credentials: 'include',
          });

          if (response.ok) {
            const data = await response.json();
            const allPreds = data.predictions || [];
            console.log(`📊 ETF Scores (legacy) [${symbol}]: Got ${allPreds.length} total predictions`);
            
            // First try to find 1d predictions
            let preds1d = allPreds.filter(p => p.timeframe === '1d');
            console.log(`  ├─ Found ${preds1d.length} 1d predictions`);
            
            // If no 1d predictions, try to use the most recent predictions (any timeframe)
            let predsToUse = preds1d.length > 0 ? preds1d : allPreds;
            
            // If we're using mixed timeframes, prefer the longest timeframe first
            if (predsToUse === allPreds && allPreds.length > 0) {
              console.log(`  ├─ No 1d found, using most recent from all timeframes`);
              predsToUse = [allPreds[0]];
            }
            
            // Sort by input_time descending
            predsToUse.sort((a, b) => new Date(b.input_time || b.created_at || 0) - new Date(a.input_time || a.created_at || 0));
            
            console.log(`  └─ Using ${predsToUse.length} predictions for calculation`);
            
            if (predsToUse.length > 0) {
              // Transform predictions into format expected by scoreCalculator
              etfDataMap[symbol] = predsToUse.map((pred, idx) => {
                const calculatedDirection = this.calculateDirection(pred);
                if (idx === 0) {
                  console.log(`  ✓ [${symbol}] Top prediction: timeframe=${pred.timeframe}, calc_dir=${calculatedDirection}, range=[${pred.prediction_low}, ${pred.prediction_high}], curr=${pred.current_price}, conf=${pred.confidence}`);
                }
                return {
                  // Direction fields (calculated from prediction range)
                  direction: calculatedDirection,
                  dir: calculatedDirection,
                  direction_1h: calculatedDirection,
                  dir_1h: calculatedDirection,
                  direction_1d: calculatedDirection,
                  dir_1d: calculatedDirection,
                  
                  // Confidence
                  confidence: (parseFloat(pred.confidence) || 0) / 100,  // Convert to 0-1
                  confPercent: (parseFloat(pred.confidence) || 0) / 100,
                  
                  // Current/market price
                  currentPrice: parseFloat(pred.current_price) || 0,
                  curr_price: parseFloat(pred.current_price) || 0,
                  current_price: parseFloat(pred.current_price) || 0,
                  price: parseFloat(pred.current_price) || 0,
                  
                  // Win rates (as percentages 0-100)
                  wr_3d: parseFloat(pred['3dwr']) || 0,
                  wr3d: parseFloat(pred['3dwr']) || 0,
                  wr_7d: parseFloat(pred['7dwr']) || 0,
                  wr7d: parseFloat(pred['7dwr']) || 0,
                  wr_30d: parseFloat(pred['30dwr']) || 0,
                  wr30d: parseFloat(pred['30dwr']) || 0,
                  wr_1d: parseFloat(pred['90dwr']) || 0,  // Use 90dwr as 1d proxy
                  wr1d: parseFloat(pred['90dwr']) || 0,
                  wr_90d: parseFloat(pred['90dwr']) || 0,
                  wr90d: parseFloat(pred['90dwr']) || 0,
                  
                  // P/L (as percentages)
                  pl_3d: parseFloat(pred['3dpl']) || 0,
                  pl3d: parseFloat(pred['3dpl']) || 0,
                  pl_7d: parseFloat(pred['7dpl']) || 0,
                  pl7d: parseFloat(pred['7dpl']) || 0,
                  pl_30d: parseFloat(pred['30dpl']) || 0,
                  pl30d: parseFloat(pred['30dpl']) || 0,
                  pl_1d: parseFloat(pred['90dpl']) || 0,  // Use 90dpl as 1d proxy
                  pl1d: parseFloat(pred['90dpl']) || 0,
                  pl_90d: parseFloat(pred['90dpl']) || 0,
                  pl90d: parseFloat(pred['90dpl']) || 0,
                  
                  // Pullback/Drawdown
                  pb_3d: Math.abs(parseFloat(pred['3dpb']) || 0),
                  pb3d: Math.abs(parseFloat(pred['3dpb']) || 0),
                  pb_7d: Math.abs(parseFloat(pred['7dpb']) || 0),
                  pb7d: Math.abs(parseFloat(pred['7dpb']) || 0),
                  pb_30d: Math.abs(parseFloat(pred['30dpb']) || 0),
                  pb30d: Math.abs(parseFloat(pred['30dpb']) || 0),
                  
                  // Metadata
                  validUntilUTC: pred.valid_until || new Date().toISOString(),
                  lastUpdated: pred.input_time || pred.created_at || new Date().toISOString(),
                  createdUTC: pred.created_at || new Date().toISOString(),
                };
              });
            }
          }
        } catch (err) {
          console.error(`Error fetching ${symbol} predictions for scores:`, err);
        }
      }

      // Try to dynamically import scoreCalculator
      try {
        // For now, return empty scores if calculator is not available
        // We'll implement calculation differently
        return ETFScoresService.calculateScoresLocally(etfDataMap);
      } catch (err) {
        console.error('Error loading scoreCalculator:', err);
        return [];
      }
    } catch (error) {
      console.error('Error in fetchETFScores:', error);
      throw error;
    }
  }

  /**
   * Calculate scores locally using 1d formula (0.8/0.2 weights)
   * Matches IBKR desktop trader's calculateETFScores1d() exactly
   * Transforms predictions API format to score format
   */
  static calculateScoresLocally(etfDataMap) {
    const scores = [];
    const etfSymbols = ['TQQQ', 'UGL', 'IBIT', 'SOXL', 'FAS', 'TMF', 'LABU', 'UCO', 'IONQ', 'CEG', 'QBTS', 'MP', 'GOOG', 'TSLA', 'AMZN', 'AAPL', 'MSFT', 'NVDA', 'META', 'FTS', 'SPY', 'WSP.TO'];
    const weight_3d = 0.8;  // 3-day weight (more recent)
    const weight_7d = 0.2;  // 7-day weight
    
    console.log('🔍 [calculateScoresLocally] Started with etfDataMap keys:', Object.keys(etfDataMap).length, 'symbols');
    
    // Helper function to safely extract metric values (handles both field naming conventions)
    const getMetric = (obj, camelCase, underscoreCase) => {
      const val = obj[camelCase] !== undefined ? obj[camelCase] : obj[underscoreCase];
      return val !== undefined && val !== null ? parseFloat(val) || 0 : 0;
    };
    
    etfSymbols.forEach(symbol => {
      const predictions = etfDataMap && etfDataMap[symbol];
      
      if (predictions && predictions.length > 0) {
        const latest = predictions[0]; // Most recent prediction
        
        // Normalize confidence from API format (already 0-100 percentage)
        // The API returns confidence as a percentage directly from database
        let confVal = latest.confidence ?? latest.confPercent ?? 0;
        if (typeof confVal === 'string') {
          confVal = parseFloat(confVal) || 0;
        }
        // API returns confidence as 0-100 percentage, keep as percentage for display
        const confPercent = confVal;
        
        const currPrice = latest.current_price || latest.currPrice || latest.curr_price || latest.currentPrice || latest.price || 0;
        
        // Determine direction from range or signal
        let direction = 'CASH';
        const high = parseFloat(latest.prediction_high) || parseFloat(latest.range_max) || 0;
        const low = parseFloat(latest.prediction_low) || parseFloat(latest.range_min) || 0;
        
        if (currPrice > 0 && (high > 0 || low > 0)) {
          const midpoint = (high + low) / 2;
          const changeRatio = (midpoint - currPrice) / currPrice;
          const threshold = 0.005;  // 0.5% threshold
          
          if (changeRatio > threshold) {
            direction = 'LONG';
          } else if (changeRatio < -threshold) {
            direction = 'SHORT';
          }
        }
        
        // Extract metrics - 3d/7d (Execution Layer)
        // Note: API returns these with leading digits (3dwr, 3dpl, etc.) due to MySQL column naming
        const wr_3d = getMetric(latest, 'wr_3d', '3dwr');
        const wr_7d = getMetric(latest, 'wr_7d', '7dwr');
        const pl_3d = getMetric(latest, 'pl_3d', '3dpl');
        const pl_7d = getMetric(latest, 'pl_7d', '7dpl');
        const pb_3d = Math.abs(getMetric(latest, 'pb_3d', '3dpb'));
        const pb_7d = Math.abs(getMetric(latest, 'pb_7d', '7dpb'));
        
        // Extract metrics - 30d (Confirmation Layer)
        const wr_30d = getMetric(latest, 'wr_30d', '30dwr');
        const pl_30d = getMetric(latest, 'pl_30d', '30dpl');
        
        // Extract metrics - 90d (Environment Layer)
        const wr_90d = getMetric(latest, 'wr_90d', '90dwr');
        const pl_90d = getMetric(latest, 'pl_90d', '90dpl');
        
        // === Daily-Based Expected Value Calculation ===
        // NOTE: CASH positions participate in normal scoring so the strongest historical 
        // performer retains its rank even when in cash (matches desktop trader behavior)
        
        // 1. Convert percentages to decimals
        const wr_3d_dec = wr_3d / 100;
        const wr_7d_dec = wr_7d / 100;
        const pl_3d_dec = pl_3d / 100;
        const pl_7d_dec = pl_7d / 100;
        
        // 2. Calculate weighted performance with negative P/L adjustment
        // When PL is negative, use (1 - WR) instead of WR for proper penalty
        const perf_3d = (pl_3d_dec < 0 ? (1 - wr_3d_dec) : wr_3d_dec) * pl_3d_dec;
        const perf_7d = (pl_7d_dec < 0 ? (1 - wr_7d_dec) : wr_7d_dec) * pl_7d_dec;
        const weighted_perf = (perf_3d * weight_3d) + (perf_7d * weight_7d);
        
        // 3. Confidence factor
        const conf_factor = confPercent / 100;
        
        // 4. Pullback adjustment (双向作用)
        const pb_weighted = (pb_3d * weight_3d) + (pb_7d * weight_7d);
        const pb_adjustment = weighted_perf >= 0 
          ? (1 - (pb_weighted / 100))      // Profit: drawdown削减
          : (1 + (pb_weighted / 100));     // Loss: drawdown放大惩罚
        
        // 5. Base score
        const score_base = (conf_factor * weighted_perf * pb_adjustment) * 10000;
        
        // 6. Non-linear expansion (makes top winners stand out)
        const score_base_expanded = (() => {
          if (score_base === 0) return 0;
          const isPositive = score_base > 0;
          const absScore = Math.abs(score_base);
          const power = 1.15;
          const expandedAbs = Math.pow(absScore, power);
          return isPositive ? expandedAbs : -expandedAbs;
        })();
        
        // 7. Apply 30d confirmation factor
        // CASH direction means no active trade - neutral factor
        let f_30d = 1.0;
        if (direction !== 'CASH' && wr_30d > 0) {
          if (pl_30d > 0 && wr_30d > 50) {
            f_30d = direction === 'LONG' ? 1.25 : 0.75;
          } else if (pl_30d < 0) {
            f_30d = direction === 'SHORT' ? 1.25 : 0.75;
          }
        }
        const score_with_30d = score_base_expanded * f_30d;
        
        // 8. Apply 90d environment filter
        let regime = 'neutral';
        let env_multiplier = 1.0;
        
        // If wr_90d is 0 or missing, treat as no data -> neutral regime
        // This matches desktop trader logic: "if wr_90d === 0 return neutral"
        if (wr_90d === 0 || pl_90d === 0) {
          regime = 'neutral';
          env_multiplier = 1.0;
        } else if (pl_90d < 0) {
          regime = 'bear';
          const loss_severity = Math.abs(pl_90d);
          const penalty_weight = 2.0;
          env_multiplier = Math.max(0.3, 1.0 - (loss_severity / 100) * penalty_weight);
        } else if (pl_90d > 0) {
          regime = 'bull';
          env_multiplier = 1.0;
        }
        const score_with_env = score_with_30d * env_multiplier;        
        // 9. Apply 1d strategic factor (90d resonance)
        // CASH direction means no active trade - neutral factor
        let f_90d = 1.0;
        if (direction !== 'CASH' && wr_90d > 0) {
          if (wr_90d > 50) {
            f_90d = 1.5;  // Strong resonance
          } else {
            f_90d = 1.2;  // Moderate resonance
          }
        }
        const final_score = score_with_env * f_90d;
        
        scores.push({
          symbol,
          dir: direction,
          conf: confPercent,
          wr_3d: wr_3d,
          pl_3d: pl_3d,
          wr_7d: wr_7d,
          pl_7d: pl_7d,
          wr_30d: wr_30d,
          pl_30d: pl_30d,
          wr_90d: wr_90d,
          pl_90d: pl_90d,
          f_30d: f_30d,
          regime: regime,
          f_90d: f_90d,
          score: final_score,
          validUntilUTC: latest.valid_until || new Date().toISOString(),
        });
      }
    });
    
    // Sort by score descending
    scores.sort((a, b) => b.score - a.score);
    
    console.log('📊 [calculateScoresLocally] Returning', scores.length, 'scores');
    
    return scores;
  }
}

export default ETFScoresService;
